/**
 * /saas/variable-consideration — ASC 606 §8 Variable Consideration dashboard.
 *
 * Shows all active VC components for the entity with:
 * - Component code + description
 * - Latest estimate (unconstrained → constrained) + as-of date
 * - Actuals vs estimate gap
 * - "Refine estimate" button → opens estimator dialog
 * - "View audit history" → estimate snapshot table
 *
 * API surface:
 *   GET  /api/saas/vc-components?active_only=true
 *   GET  /api/saas/vc-components/{id}/estimates
 *   GET  /api/saas/vc-components/{id}/actuals?period_start=&period_end=
 *   POST /api/saas/vc-components
 *   POST /api/saas/vc-estimates
 *   POST /api/saas/vc-actuals
 *   GET  /api/saas/vc-trueup/{period_id}/preview
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import HistoryIcon from "@mui/icons-material/History";
import EditNoteIcon from "@mui/icons-material/EditNote";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import { api, errorMessage } from "@/lib/api";

export const Route = createFileRoute("/_auth/saas/variable-consideration")({
  component: VariableConsiderationPage,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type VcComponent = {
  id: string;
  contract_obligation_id: string;
  component_code: string;
  description: string | null;
  estimation_method: string;
  direction: string;
  constraint_pct: string;
  constraint_basis: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

type VcEstimate = {
  id: string;
  vc_component_id: string;
  period_start: string;
  period_end: string;
  as_of_date: string;
  unconstrained_estimate_cents: number;
  constraint_pct_applied: string;
  constrained_amount_cents: number;
  confidence: string;
  reasoning: string | null;
  ai_generated: boolean;
  superseded_by_id: string | null;
};

type VcActual = {
  id: string;
  event_date: string;
  amount_cents: number;
  event_kind: string;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const cents = (n: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n / 100);

const COMPONENT_LABELS: Record<string, string> = {
  usage_metered: "Usage metered",
  volume_rebate: "Volume rebate",
  performance_bonus: "Performance bonus",
  sla_credit: "SLA credit",
  refund_estimate: "Refund estimate",
  mfn_clawback: "MFN clawback",
  other: "Other",
};

const CONFIDENCE_COLOR: Record<string, "success" | "warning" | "error"> = {
  high: "success",
  medium: "warning",
  low: "error",
};

function CatchupIndicator({
  estimateCents,
  actualsCents,
}: {
  estimateCents: number;
  actualsCents: number;
}) {
  const delta = actualsCents - estimateCents;
  const pct = estimateCents > 0 ? Math.abs(delta / estimateCents) * 100 : 0;

  if (Math.abs(delta) < 100) {
    return (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <TrendingFlatIcon fontSize="small" color="disabled" />
        <Typography variant="body2" color="text.secondary">
          On estimate
        </Typography>
      </Stack>
    );
  }

  const color = delta > 0 ? "success.main" : "error.main";
  const Icon = delta > 0 ? TrendingUpIcon : TrendingDownIcon;
  const label = delta > 0 ? `+${cents(delta)}` : cents(delta);

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Icon fontSize="small" sx={{ color }} />
      <Typography variant="body2" sx={{ color }}>
        {label} ({pct.toFixed(0)}%)
      </Typography>
    </Stack>
  );
}

// ─── Estimate history dialog ──────────────────────────────────────────────────

function EstimateHistoryDialog({
  open,
  component,
  onClose,
}: {
  open: boolean;
  component: VcComponent | null;
  onClose: () => void;
}) {
  const { data: estimates, isLoading } = useQuery({
    queryKey: ["vc-estimates", component?.id],
    queryFn: () =>
      api<VcEstimate[]>(`/api/saas/vc-components/${component!.id}/estimates`),
    enabled: open && !!component,
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Estimate history — {component ? COMPONENT_LABELS[component.component_code] : ""}
      </DialogTitle>
      <DialogContent>
        {isLoading && <CircularProgress />}
        {estimates && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>As-of date</TableCell>
                <TableCell>Period</TableCell>
                <TableCell align="right">Unconstrained</TableCell>
                <TableCell align="center">Constraint %</TableCell>
                <TableCell align="right">Constrained</TableCell>
                <TableCell>Confidence</TableCell>
                <TableCell>AI?</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {estimates.map((est: VcEstimate) => (
                <TableRow
                  key={est.id}
                  sx={{
                    opacity: est.superseded_by_id ? 0.5 : 1,
                    backgroundColor: est.superseded_by_id
                      ? "action.hover"
                      : undefined,
                  }}
                >
                  <TableCell>{est.as_of_date}</TableCell>
                  <TableCell sx={{ fontSize: 11, color: "text.secondary" }}>
                    {est.period_start} – {est.period_end}
                  </TableCell>
                  <TableCell align="right">
                    {cents(est.unconstrained_estimate_cents)}
                  </TableCell>
                  <TableCell align="center">
                    {(parseFloat(est.constraint_pct_applied) * 100).toFixed(0)}%
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {cents(est.constrained_amount_cents)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={est.confidence}
                      color={CONFIDENCE_COLOR[est.confidence] ?? "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {est.ai_generated ? "✨ AI" : "Manual"}
                  </TableCell>
                  <TableCell>
                    {est.superseded_by_id ? (
                      <Chip label="Superseded" size="small" variant="outlined" />
                    ) : (
                      <Chip label="Current" size="small" color="primary" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── VC Component card ────────────────────────────────────────────────────────

function VcComponentCard({
  component,
  onViewHistory,
}: {
  component: VcComponent;
  onViewHistory: (c: VcComponent) => void;
}) {
  const today = new Date();
  const periodStart = new Date(today.getFullYear(), today.getMonth(), 1)
    .toISOString()
    .slice(0, 10);
  const periodEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    .toISOString()
    .slice(0, 10);

  const { data: estimates } = useQuery({
    queryKey: ["vc-estimates", component.id],
    queryFn: () =>
      api<VcEstimate[]>(`/api/saas/vc-components/${component.id}/estimates`),
  });

  const { data: actuals } = useQuery({
    queryKey: ["vc-actuals", component.id, periodStart],
    queryFn: () =>
      api<VcActual[]>(
        `/api/saas/vc-components/${component.id}/actuals?period_start=${periodStart}&period_end=${periodEnd}`
      ),
  });

  const latest = estimates?.find((e) => !e.superseded_by_id);
  const totalActuals = actuals?.reduce((sum, a) => sum + a.amount_cents, 0) ?? 0;

  return (
    <Card variant="outlined">
      <CardHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <Chip
              label={COMPONENT_LABELS[component.component_code] ?? component.component_code}
              size="small"
              variant="filled"
              color="primary"
            />
            <Chip
              label={component.direction}
              size="small"
              variant="outlined"
              color={component.direction === "additive" ? "success" : "error"}
            />
            <Chip
              label={component.estimation_method.replace("_", " ")}
              size="small"
              variant="outlined"
            />
          </Stack>
        }
        subheader={component.description ?? "No description"}
        action={
          <Tooltip title="View audit history">
            <IconButton size="small" onClick={() => onViewHistory(component)}>
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        }
      />
      <CardContent>
        <Stack spacing={1.5}>
          {latest ? (
            <>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Latest estimate (as of {latest.as_of_date})
                </Typography>
                {latest.ai_generated && (
                  <Chip label="✨ AI" size="small" variant="outlined" />
                )}
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Unconstrained
                  </Typography>
                  <Typography variant="body1">
                    {cents(latest.unconstrained_estimate_cents)}
                  </Typography>
                </Box>
                <Typography color="text.secondary">→</Typography>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Constrained (
                    {(parseFloat(latest.constraint_pct_applied) * 100).toFixed(0)}%)
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {cents(latest.constrained_amount_cents)}
                  </Typography>
                </Box>
                <Box ml="auto">
                  <Chip
                    label={latest.confidence}
                    color={CONFIDENCE_COLOR[latest.confidence] ?? "default"}
                    size="small"
                  />
                </Box>
              </Stack>
            </>
          ) : (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              No estimate yet. Click Refine estimate to add one.
            </Alert>
          )}

          <Divider />

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="caption" color="text.secondary">
                MTD actuals
              </Typography>
              <Typography variant="body1">{cents(totalActuals)}</Typography>
            </Box>
            {latest && (
              <CatchupIndicator
                estimateCents={latest.constrained_amount_cents}
                actualsCents={totalActuals}
              />
            )}
          </Stack>

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              size="small"
              startIcon={<EditNoteIcon />}
              variant="outlined"
              disabled
            >
              Refine estimate
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function VariableConsiderationPage() {
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState<VcComponent | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data: components, isLoading, error } = useQuery({
    queryKey: ["vc-components"],
    queryFn: () =>
      api<VcComponent[]>("/api/saas/vc-components?active_only=true"),
  });

  const handleViewHistory = (comp: VcComponent) => {
    setSelectedComponent(comp);
    setHistoryOpen(true);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Variable Consideration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ASC 606 §8 — usage-based, bonus, rebate &amp; refund estimates with constraint
          </Typography>
        </Box>
        <Button startIcon={<AddIcon />} variant="contained" disabled>
          Add component
        </Button>
      </Stack>

      {isLoading && (
        <Box display="flex" justifyContent="center" pt={6}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error">{errorMessage(error)}</Alert>
      )}

      {components && components.length === 0 && (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No variable consideration components yet
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Variable consideration applies when contract terms include usage billing,
            performance bonuses, volume rebates, SLA credits, or right-of-return
            estimates (ASC 606-10-32-5).
          </Typography>
          <Typography variant="body2" color="text.secondary">
            To add a VC component, open a subscription, go to the{" "}
            <strong>Variable Consideration</strong> tab, and click Add component.
          </Typography>
        </Paper>
      )}

      {components && components.length > 0 && (
        <Stack
          display="grid"
          sx={{ gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))", gap: 2 }}
        >
          {components.map((comp) => (
            <VcComponentCard
              key={comp.id}
              component={comp}
              onViewHistory={handleViewHistory}
            />
          ))}
        </Stack>
      )}

      <EstimateHistoryDialog
        open={historyOpen}
        component={selectedComponent}
        onClose={() => setHistoryOpen(false)}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast}
      />
    </Box>
  );
}
